import { Button } from "@/components/ui/button";
import { defaultButtonClasses } from "@/constants/ui";
import { generateOccurrenceList } from "@/components/tpr/RecurrenceExceptions/generateOccurrenceList";
import { TreePalm } from "lucide-react";
import type { Booking } from "@/types/Booking";

interface SlotOccurrencesListProps {
  booking: Booking;
  canEditBooking: boolean;
  onTakeSlotHoliday: () => void;
}

/**
 * Lists a slot booking's occurrences (last few past + upcoming) for the booking view.
 * For elevated users the "Take slot holiday" action sits ABOVE the list so it's reachable
 * without scrolling past a long list of occurrences.
 */
export const SlotOccurrencesList = ({ booking, canEditBooking, onTakeSlotHoliday }: SlotOccurrencesListProps) => {
  const recurrence = booking.recurrence;
  if (!recurrence) return null;

  // Last 3 past + up to 52 future (capped at 1-year forward by the helper for unbounded series).
  const entries = generateOccurrenceList(new Date(booking.date), recurrence);

  if (entries.length === 0) return null;

  const formatRow = (date: Date, state: "past" | "excluded" | "available") => {
    const dayName = date.toLocaleDateString("en-GB", { weekday: "short" });
    const day = date.getDate();
    const month = date.toLocaleDateString("en-GB", { month: "long" });
    const ordinal = (n: number): string => {
      if (n >= 11 && n <= 13) return "th";
      switch (n % 10) {
        case 1: return "st";
        case 2: return "nd";
        case 3: return "rd";
        default: return "th";
      }
    };
    const year = date.getFullYear();
    const todayYear = new Date().getFullYear();
    const yearSuffix = year === todayYear ? "" : ` ${year}`;
    const dateStr = `${dayName}, ${day}${ordinal(day)} ${month}${yearSuffix}`;
    return `${dateStr} at ${booking.startTime.slice(0, 2)}:${booking.startTime.slice(2)}${state === "excluded" ? " (excluded)" : state === "past" ? " (past)" : ""}`;
  };

  return (
    <div className="bg-gray-100 p-3 rounded-md mt-2" data-testid="slot-occurrences-list">
      {canEditBooking && (
        <div className="flex flex-col items-end mb-3">
          <Button
            variant="default"
            size="sm"
            className={defaultButtonClasses}
            onClick={onTakeSlotHoliday}
            data-testid="take-slot-holiday-button"
          >
            <TreePalm /> Take slot holiday
          </Button>
        </div>
      )}
      <p className="text-sm font-semibold text-gray-700 mb-2">Occurrences</p>
      <ul className="text-sm text-gray-700 divide-y divide-gray-200 max-h-96 overflow-y-auto">
        {entries.map((entry) => {
          const muted = entry.state === "past";
          const struck = entry.state === "excluded";
          return (
            <li
              key={entry.iso}
              data-testid={`slot-occurrence-${entry.iso}`}
              data-state={entry.state}
              className={`py-1.5 px-2 ${muted ? "text-gray-400" : ""} ${struck ? "line-through text-gray-500" : ""}`}
            >
              {formatRow(entry.date, entry.state)}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
